import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Spinner from '../common/spinner';
import CourseCard from '../components/courseCard';

const Courses = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState('all');
    const [visibleCount, setVisibleCount] = useState(6);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const categoryFromUrl = params.get('category');
        if (categoryFromUrl) {
            setCategory(categoryFromUrl);
        }
    }, [location.search]);

    useEffect(() => {
        const fetchCourses = async () => {
            setLoading(true);
            try {
                const { data } = await api.get('/courses/available');
                setCourses(data);
                setError(null);
            } catch (err) {
                setError('Failed to load courses. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    const handleEnroll = (courseId) => {
        navigate(`/courses/${courseId}/enroll`);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const filteredCourses = courses.filter(course => {
        const matchesCategory = category === 'all' || course.category === category;
        const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const paginatedCourses = filteredCourses.slice(0, visibleCount);
    const categories = ['all', 'programming', 'design', 'business', 'marketing', 'music', 'other'];
    const isHomeEmbed = location.pathname === '/';

    if (loading) return <Spinner />;
    if (error) return <div className="alert alert-danger text-center">{error}</div>;

    return (
        <section
            className="fundisha-courses-section"
            style={{ paddingTop: isHomeEmbed ? '3.5rem' : 'calc(var(--af-nav-height) + 2.2rem)' }}
        >
            <header className="fundisha-section-header wow fadeInUp" data-wow-delay="0.1s">
                <p className="fundisha-section-title">Courses</p>
                <h1 className="fundisha-section-heading">Build Skills For The Future</h1>
            </header>

            <div className="fundisha-courses-toolbar">
                <input
                    type="text"
                    className="fundisha-input"
                    placeholder="Search for courses"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                    className="fundisha-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                >
                    {categories.map((cat) => (
                        <option key={cat} value={cat}>
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                    ))}
                </select>
                <button
                    className="fundisha-btn-ghost"
                    type="button"
                    onClick={() => {
                        setSearchTerm('');
                        setCategory('all');
                    }}
                >
                    Clear
                </button>
            </div>

            {paginatedCourses.length > 0 ? (
                <div className="fundisha-courses-grid" role="list">
                    {paginatedCourses.map((course, index) => (
                        <CourseCard
                            key={course.id}
                            id={course.id}
                            price={formatPrice(course.price)}
                            title={course.title}
                            description={course.description}
                            instructor={course.instructor ? course.instructor.name : 'N/A'}
                            duration={`${course.duration}h`}
                            students={(course.enrolledStudents || []).length}
                            level={course.level}
                            category={course.category}
                            onEnroll={handleEnroll}
                            index={index}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-center" style={{ color: 'var(--af-text-muted)' }}>
                    No courses found. Try adjusting your search or filter.
                </p>
            )}

            {visibleCount < filteredCourses.length && (
                <div className="text-center mt-5">
                    <button
                        onClick={() => setVisibleCount((prev) => prev + 6)}
                        className="fundisha-btn-primary"
                        type="button"
                    >
                        Load More
                    </button>
                </div>
            )}
        </section>
    );
};

export default Courses;